import { SyntheticEvent, useEffect, useRef, useState, WheelEvent } from "react";
import styles from "./DataTable.module.css";
import TextInput from "../common/TextInput";
import CSVTextAreaEditor from "./CSVTextAreaEditor";

interface DataTableProps {
    mapData: string[][] | undefined;
    setMapData?: React.Dispatch<React.SetStateAction<string[][] | undefined>>;
    setFailMapData?: React.Dispatch<React.SetStateAction<string[][]>>;
    isFailMapData?: boolean;
    inferredAddressFieldIndices: number[] | undefined;
    fieldIdx?: number;
    isUserDataEditMode?: boolean;
    onCompleteUserEdit?: (text: string) => void;
    onCancleUserEdit?: () => void;
}
/**
 * @author cjs
 * @date 2024-08-27 16:39:21
 * @remark 지도만들기 데이터 테이블 컴포넌트
 * @param props
 * @props DataTableProps
 *          mapData(required) 지도 데이터
 * @returns <div>
 */
function DataTable(props: DataTableProps) {
    const [changeInput, setChangeInput] = useState<boolean>(false);
    const [editingCell, setEditingCell] = useState<{ rowIdx: number; colIdx: number } | null>(null); // 클릭한 셀 정보 저장
    const inputRef = useRef<HTMLInputElement>(null);
    const spanRef = useRef<HTMLSpanElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const columnRefs = useRef<(HTMLTableCellElement | null)[]>([]);

    useEffect(() => {}, [props.mapData]);

    const wheelEvent = (event: WheelEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (!scrollRef.current) return;
        scrollRef.current.scrollLeft += event.deltaY;
    };

    // 실패 주소 목록 테이블 클릭 이벤트
    const clickFailData = (rowIdx: number, colIdx: number) => {
        // 셀 클릭 시 해당 셀을 수정 가능한 상태로 설정
        setEditingCell({ rowIdx, colIdx });
        setChangeInput(true); // 입력 모드로 변경
    };

    // text input 변경 함수
    const handleChangeInput = (rowIdx: number, colIdx: number, e: SyntheticEvent) => {
        let inputValue = e.target as HTMLInputElement;
        if (props.mapData) {
            let _mapData: string[][] = [...props.mapData];
            _mapData[rowIdx][colIdx] = inputValue.value;
            props.setFailMapData && props.setFailMapData(_mapData);
        }
    };

    // 마우스 클릭 이벤트
    const handleClickInput = (e: MouseEvent) => {
        // 클릭된 타겟이 inputRef 또는 spanRef에 포함되지 않으면, 입력모드 종료
        if (changeInput) {
            if (changeInput && inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setChangeInput(false);
                setEditingCell(null);
            }
        }
    };

    // 주소 수정 후 엔터키 입력
    const activeEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            setChangeInput(false);
            setEditingCell(null);
        }
    };

    useEffect(() => {
        window.addEventListener("mousedown", handleClickInput);

        // cleanup function
        return () => {
            window.removeEventListener("mousedown", handleClickInput);
        };
    }, [changeInput]);

    const renderTableItem = (mapData: string[][]) => {
        return (
            <table className={styles.subTable}>
                <thead className={styles.subTableHead}>
                    <tr>
                        {mapData[0].map((colData, colIdx) => {
                            return <th key={colIdx}>{colData}</th>;
                        })}
                    </tr>
                </thead>
                <tbody className={styles.subTableBody}>
                    {mapData.map((rowData, rowIdx) => {
                        if (rowIdx !== 0 && rowIdx <= 5) {
                            return (
                                <tr key={rowIdx}>
                                    {rowData.map((colData, colIdx) => {
                                        const isEditing = editingCell?.rowIdx === rowIdx && editingCell?.colIdx === colIdx;
                                        return (
                                            <td
                                                onClick={() => clickFailData(rowIdx, colIdx)}
                                                key={colIdx}
                                                className={`${
                                                    props.isFailMapData &&
                                                    props.inferredAddressFieldIndices &&
                                                    props.inferredAddressFieldIndices.includes(colIdx) &&
                                                    styles.failAddress
                                                }
                                                            ${
                                                                !props.isFailMapData &&
                                                                props.inferredAddressFieldIndices &&
                                                                props.inferredAddressFieldIndices.includes(colIdx) &&
                                                                styles.successAddress
                                                            }`}
                                            >
                                                {props.isFailMapData &&
                                                props.inferredAddressFieldIndices &&
                                                props.inferredAddressFieldIndices.includes(colIdx) ? (
                                                    <>
                                                        {!isEditing ? (
                                                            <span ref={spanRef} style={{ width: spanRef.current?.getBoundingClientRect().width }}>
                                                                {colData}
                                                            </span>
                                                        ) : (
                                                            <TextInput
                                                                type="text"
                                                                value={colData}
                                                                ref={inputRef}
                                                                onChange={(e) => handleChangeInput(rowIdx, colIdx, e)}
                                                                width={String(spanRef.current?.getBoundingClientRect().width)}
                                                                fontSize="14px"
                                                                onKeyDown={(e) => activeEnter(e)}
                                                            />
                                                        )}
                                                    </>
                                                ) : (
                                                    colData
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        }
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div className={`${styles.wrapTable} ${props.isFailMapData && styles.failMapData}`} onWheel={wheelEvent} ref={scrollRef}>
            {props.mapData && props.mapData.length > 0 && !props.isUserDataEditMode && renderTableItem(props.mapData)}
            {props.mapData && props.mapData.length > 0 && props.isUserDataEditMode && (
                <CSVTextAreaEditor
                    mapData={props.mapData}
                    onComplete={(updatedData) => {
                        console.log(`updatedData: ${updatedData}`);
                        if (props.onCompleteUserEdit) {
                            props.onCompleteUserEdit(updatedData);
                        }
                    }}
                    onCancle={() => {
                        if (props.onCancleUserEdit) {
                            props.onCancleUserEdit();
                        }
                    }}
                />
            )}
        </div>
    );
}

export default DataTable;
